/**
 * JsonEditor - A reusable CodeMirror-based JSON editor component
 *
 * Provides syntax highlighting, bracket matching, line numbers, and
 * theme support (light/dark) via Fluent UI tokens.
 */

import { useEffect, useRef, useCallback, memo } from 'react';
import { makeStyles, shorthands, tokens, mergeClasses } from '@fluentui/react-components';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { bracketMatching, foldGutter, foldKeymap } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { useAppStore } from '@/stores/appStore';
import { createFluentTheme, formatJsonKeymap, wrapSelectionInputHandler } from '@/components/WorkflowDesigner/CodeEditor/extensions';

const useStyles = makeStyles({
  container: {
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: 'hidden',
    '& .cm-editor': {
      height: '100%',
    },
    '& .cm-scroller': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase300,
    },
  },
  /**
   * Fill mode — expands to its flex parent. Requires the parent to have a
   * definite height (or `flex: 1` inside another flex column with
   * `min-height: 0`). Use instead of `minHeight`/`maxHeight` when the editor
   * should consume all remaining space and scroll internally.
   */
  fill: {
    flex: 1,
    minHeight: 0,
    height: '100%',
  },
  error: {
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
  },
});

export interface JsonEditorProps {
  /** Current JSON value as a string */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Placeholder text shown when empty */
  placeholder?: string;
  /** Minimum height in pixels. Ignored when `fill` is true. */
  minHeight?: number;
  /**
   * Optional maximum height in pixels. When omitted (the default), the editor
   * grows with its content and lets the parent decide how to handle overflow.
   * Ignored when `fill` is true.
   */
  maxHeight?: number;
  /**
   * When true, the editor fills its flex parent and scrolls internally.
   * The parent must be a flex container with a definite height (or part of a
   * flex chain that resolves to one) and ideally `min-height: 0`.
   */
  fill?: boolean;
  /** Whether to show an error state (red border) */
  hasError?: boolean;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Additional class name */
  className?: string;
}

export const JsonEditor = memo(({
  value,
  onChange,
  minHeight = 150,
  maxHeight,
  fill = false,
  hasError = false,
  readOnly = false,
  className,
}: JsonEditorProps) => {
  const styles = useStyles();
  const theme = useAppStore((s) => s.theme);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());
  const isUpdatingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Create editor on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      foldGutter({
        openText: '▼',
        closedText: '▶',
      }),
      bracketMatching(),
      wrapSelectionInputHandler(),
      closeBrackets(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      search({ top: true }),
      json(),
      EditorState.tabSize.of(2),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...searchKeymap,
        ...formatJsonKeymap,
        indentWithTab,
      ]),
      themeCompartment.current.of(createFluentTheme(theme)),
      readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !isUpdatingRef.current) {
          onChangeRef.current(update.state.doc.toString());
        }
      }),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Only run on mount - value changes are handled via setValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update theme when it changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: themeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  // Update read-only state when it changes
  useEffect(() => {
    if (!viewRef.current) return;
    viewRef.current.dispatch({
      effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)),
    });
  }, [readOnly]);

  // Sync value from props to editor
  const setValue = useCallback((newValue: string) => {
    if (!viewRef.current) return;
    const currentValue = viewRef.current.state.doc.toString();
    if (currentValue === newValue) return;

    isUpdatingRef.current = true;
    viewRef.current.dispatch({
      changes: {
        from: 0,
        to: viewRef.current.state.doc.length,
        insert: newValue,
      },
    });
    isUpdatingRef.current = false;
  }, []);

  // Update editor when value prop changes
  useEffect(() => {
    setValue(value);
  }, [value, setValue]);

  return (
    <div
      ref={containerRef}
      className={mergeClasses(
        styles.container,
        fill && styles.fill,
        hasError && styles.error,
        className
      )}
      style={fill ? undefined : { minHeight, ...(maxHeight !== undefined ? { maxHeight } : {}) }}
    />
  );
});

JsonEditor.displayName = 'JsonEditor';
